/**
 * Door / window glyphs of organisation definitions, in the opening's LOCAL
 * frame (x = u along the wall, y = n across it; see ObjectOpening in
 * shared-types). openingGlyphs.ts mirrors them for the hinge / side and maps
 * them onto the wall (bending along curved walls).
 */

import type { ObjectDefinitionSpec, ObjectShapeLayer } from 'shared-types';
import { evaluateCustomGlyph, type CustomParamValues } from '../evaluateCustomGlyph';

export interface CustomOpeningLive {
  /** Opening width along the wall (m). */
  width: number;
  /** Wall thickness at the opening (m); the spec default when unknown. */
  wall?: number;
  /** Opening height (m) — expressions' `height`. */
  height?: number;
  params?: CustomParamValues;
  /** When given, dashed lines are collected here undashed (see evaluateCustomGlyph). */
  dashedOut?: number[];
}

/**
 * Glyph segments (`[u1,n1,0, u2,n2,0, …]`) of a door / window at `live.width`,
 * hinge at −u, opening toward +n. The depth follows the width in the
 * default proportion, so `stretch` across the wall scales with the width.
 */
export function evaluateCustomOpeningLocal(spec: ObjectDefinitionSpec, live: CustomOpeningLive, layer: ObjectShapeLayer = 'glyph'): number[] {
  const { width } = live;
  const ratio = spec.defaultSize.width > 1e-9 ? spec.defaultSize.depth / spec.defaultSize.width : 1;
  return evaluateCustomGlyph(spec, { center: { x: 0, y: 0 }, dir: { x: 1, y: 0 }, norm: { x: 0, y: 1 }, width, depth: width * ratio, z: 0 }, live.params ?? {}, layer, {
    height: live.height,
    wall: live.wall,
    dashedOut: live.dashedOut,
  });
}

/** Mirror local segments in place: u for the hinge at the far end, n for the other side. */
export function mirrorOpeningSegments(segments: number[], mirrorU: boolean, mirrorN: boolean): number[] {
  if (!mirrorU && !mirrorN) return segments;
  for (let i = 0; i + 2 < segments.length; i += 3) {
    if (mirrorU) segments[i] = -segments[i];
    if (mirrorN) segments[i + 1] = -segments[i + 1];
  }
  return segments;
}
